/**
 * Native capability abstraction layer.
 * Automatically uses Capacitor plugins on native platforms,
 * falls back to Web APIs on browser.
 */
import { Capacitor } from '@capacitor/core';

const isNative = () => Capacitor.isNativePlatform();

/**
 * Convert ArrayBuffer to base64 string without using spread operator.
 * The spread approach `String.fromCharCode(...new Uint8Array(buf))` crashes
 * on large buffers because it exceeds the JS call stack size limit.
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const CHUNK = 8192;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const chunk = bytes.subarray(i, Math.min(i + CHUNK, bytes.length));
    binary += String.fromCharCode.apply(null, chunk as unknown as number[]);
  }
  return btoa(binary);
}

// ─── Haptics ────────────────────────────────────────────
export const haptic = async (style: 'light' | 'medium' | 'heavy' = 'light') => {
  try {
    if (isNative()) {
      const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
      const map = { light: ImpactStyle.Light, medium: ImpactStyle.Medium, heavy: ImpactStyle.Heavy };
      await Haptics.impact({ style: map[style] });
    } else {
      const ms = style === 'light' ? 8 : style === 'medium' ? 15 : 25;
      navigator.vibrate?.(ms);
    }
  } catch {}
};

// ─── Share ──────────────────────────────────────────────
interface NativeShareOptions {
  title?: string;
  text?: string;
  url?: string;
  files?: File[];
}

export const nativeShare = async (options: NativeShareOptions): Promise<boolean> => {
  try {
    if (isNative()) {
      const { Share } = await import('@capacitor/share');
      // For files, we need to save them first via Filesystem
      if (options.files?.length) {
        const { Filesystem, Directory } = await import('@capacitor/filesystem');
        const file = options.files[0];
        const arrayBuffer = await file.arrayBuffer();
        const base64 = arrayBufferToBase64(arrayBuffer);
        const fileName = file.name || `share-${Date.now()}.png`;
        const saved = await Filesystem.writeFile({
          path: fileName,
          data: base64,
          directory: Directory.Cache,
        });
        await Share.share({
          title: options.title,
          url: saved.uri,
        });
      } else {
        await Share.share({
          title: options.title,
          text: options.text,
          url: options.url,
        });
      }
      return true;
    } else {
      // Web fallback
      if (navigator.share) {
        const shareData: ShareData = { title: options.title, text: options.text, url: options.url };
        if (options.files?.length && navigator.canShare?.({ files: options.files })) {
          shareData.files = options.files;
        }
        await navigator.share(shareData);
        return true;
      }
      return false;
    }
  } catch (e: any) {
    if (e?.name === 'AbortError') return true; // user cancelled
    console.error('Share error:', e);
    return false;
  }
};

// ─── Clipboard ──────────────────────────────────────────
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (isNative()) {
      const { Clipboard } = await import('@capacitor/clipboard');
      await Clipboard.write({ string: text });
      return true;
    } else {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Final fallback: textarea hack
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      return true;
    } catch { return false; }
  }
};

// ─── File Save (Image) ─────────────────────────────────
export const saveImageBlob = async (blob: Blob, filename: string): Promise<boolean> => {
  try {
    if (isNative()) {
      // First write to a temp file, then save to photo gallery
      const { Filesystem, Directory } = await import('@capacitor/filesystem');
      const arrayBuffer = await blob.arrayBuffer();
      const base64 = arrayBufferToBase64(arrayBuffer);
      const tempPath = `tmp_share_${Date.now()}.png`;
      const saved = await Filesystem.writeFile({
        path: tempPath,
        data: base64,
        directory: Directory.Cache,
      });

      try {
        // Use @capacitor-community/media to save to photo gallery
        const { Media } = await import('@capacitor-community/media');
        await Media.savePhoto({ path: saved.uri });
      } finally {
        // Clean up temp file
        Filesystem.deleteFile({ path: tempPath, directory: Directory.Cache }).catch(() => {});
      }
      return true;
    } else {
      // Web fallback: <a download>
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return true;
    }
  } catch (e) {
    console.error('Save image error:', e);
    return false;
  }
};

// ─── Share or Save Image ────────────────────────────────
/** Try to share the blob image; if sharing fails/unavailable, save it locally */
export const shareOrSaveImage = async (blob: Blob, filename: string, title?: string): Promise<'shared' | 'saved' | 'failed'> => {
  const file = new File([blob], filename, { type: blob.type || 'image/png' });
  const shared = await nativeShare({ files: [file], title });
  if (shared) return 'shared';
  const saved = await saveImageBlob(blob, filename);
  return saved ? 'saved' : 'failed';
};

// ─── Open URL (for OAuth etc.) ──────────────────────────
export const openExternalUrl = async (url: string): Promise<void> => {
  if (isNative()) {
    const { Browser } = await import('@capacitor/browser');
    await Browser.open({ url, presentationStyle: 'popover' });
  } else {
    window.location.href = url;
  }
};
